import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { usePhoenixRefresh } from './usePhoenixRefresh'
import { useQueueByTable } from './useQueueByTable'
import { enqueueMutation } from '../lib/mutationQueue'
import { getCached, setCached } from '../lib/hookCache'

const TABLE = 'body_measurements'
const CACHE_SCOPE = 'body_measurements'

export function useBodyMeasurements() {
  const { user } = useAuth()
  const cached = getCached(CACHE_SCOPE, user?.id)
  const [serverMeasurements, setServerMeasurements] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const pending = useQueueByTable(TABLE)
  const initialLoadedRef = useRef(!!cached)

  const fetchMeasurements = useCallback(async () => {
    if (!supabaseConfigured) {
      setError('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your .env file.')
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from(TABLE)
      .select('*')
      .order('date', { ascending: false })

    if (err) {
      setError(err.message)
    } else {
      const next = data || []
      setServerMeasurements(next)
      if (user) setCached(CACHE_SCOPE, user.id, next)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => {
    fetchMeasurements()
  }, [fetchMeasurements])

  usePhoenixRefresh(fetchMeasurements)

  const measurements = useMemo(() => {
    let view = [...serverMeasurements]
    for (const m of pending) {
      if (m.op === 'upsert') {
        view.unshift({ id: m.id, ...m.payload, _pending: m })
      } else if (m.op === 'delete') {
        view = view.filter((e) => e.id !== m.payload.id)
      }
    }
    view.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    return view
  }, [serverMeasurements, pending])

  const addMeasurement = async (data) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { ...data, user_id: user.id },
      userId: user.id,
    })
    return true
  }

  const deleteMeasurement = async (id) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      id,
      table: TABLE,
      op: 'delete',
      payload: { id },
      userId: user.id,
    })
    return true
  }

  return { measurements, loading, error, addMeasurement, deleteMeasurement, refetch: fetchMeasurements }
}
